import React, { useState, useEffect } from 'react';
import { Text, TextInput, Checkbox, Button } from 'mintel-react-ui';

import useTranslation from '../translations';
import { StringField, BoolField } from '../fields';
import PageWrapper from '../PageWrapper';
import styles from './LoginForm.scss';
import { handleEmailSubmit, changeLanguage } from '../shared/utils';
import { HiddenFields } from '../InputWrapper';
import Footer from './Subcomponents';

export type LoginFormProps = {
  showPasswordField: boolean;
  showUsernameField?: boolean;
  errors: string[];
  info: string[];
  title: string;
  fields: {
    username: StringField;
    remember: BoolField;
    // eslint-disable-next-line camelcase
    current_step: StringField;
    csrf: StringField;
    password?: StringField;
  };
  description?: string;
  buttonText?: string;
  hideFooter?: boolean;
  forgotPasswordLink?: string;
  fedLoginLink?: string;
  registerLink?: string;
  language?: string;
};

const LoginForm = ({
  showPasswordField,
  showUsernameField = true,
  errors,
  info,
  title,
  fields,
  description,
  buttonText,
  hideFooter = false,
  forgotPasswordLink,
  fedLoginLink,
  registerLink = '',
  language = 'en',
}: LoginFormProps): JSX.Element => {
  const { t } = useTranslation();
  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  const [rememberUser, setRememberUser] = useState<boolean>(true);
  const [email, setEmail] = useState<string>(fields.username.value);
  const [password, setPassword] = useState<string>(
    fields.password?.value || '',
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (
    e: React.SyntheticEvent | React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (showUsernameField) {
      handleEmailSubmit(e, email, t, setError);
    }
    if (showPasswordField && !password) {
      e.preventDefault();
    }
  };

  return (
    <PageWrapper errors={errors} infos={info}>
      <div className={styles.form} data-testId="loginForm">
        <Text.Heading2>{title}</Text.Heading2>
        {description && (
          <div className={styles.description}>
            <Text.Body2>{description}</Text.Body2>
          </div>
        )}
        <form method="POST">
          {showUsernameField && (
            <div className={styles.textInput}>
              <TextInput
                id={fields.username.id}
                name={fields.username.name}
                showLabel
                showClear={false}
                label={t('Email Address')}
                value={email}
                placeholder={t('Enter your email address')}
                showIcon={false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                }}
                onSubmit={(e: React.SyntheticEvent) => {
                  handleSubmit(e);
                }}
                error={error}
              />
            </div>
          )}
          {showPasswordField && (
            <>
              {!showUsernameField && (
                <>
                  <span className={styles.label}>
                    <Text.Link color={styles.labelColor}>
                      {t('Email Address')}
                    </Text.Link>
                  </span>
                  <span className={styles.email}>
                    <Text.Heading6>
                      <b>{fields.username.value}</b>
                    </Text.Heading6>
                  </span>
                  {/* eslint-disable jsx-a11y/anchor-is-valid */}
                  <Text.Body3>
                    <a href="">{`(${t('change email')})`}</a>
                  </Text.Body3>
                </>
              )}
              <div className={styles.textInput}>
                <TextInput
                  id={fields.password?.id}
                  name={fields.password?.name}
                  showLabel
                  showClear={false}
                  label={t('Password')}
                  value={password}
                  password
                  placeholder={t('Enter your password')}
                  showIcon={false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target.value);
                  }}
                  onSubmit={(e: React.SyntheticEvent) => {
                    handleSubmit(e);
                  }}
                />
              </div>
            </>
          )}
          {fields.remember && !fields.remember?.hidden && (
            <>
              <input
                hidden
                type="checkbox"
                name={fields.remember.name}
                checked={rememberUser}
                onChange={() => {
                  // do nothing
                }}
              />
              <div className={styles.checkbox}>
                <Checkbox
                  checked={rememberUser}
                  label={t('Remember me')}
                  labelVariant="Body2"
                  onChange={() => {
                    setRememberUser(!rememberUser);
                  }}
                />
              </div>
            </>
          )}
          {showPasswordField && (
            <div className={styles.forgotPasswordLink}>
              <Text.Body2>
                <a
                  href={`${forgotPasswordLink}?email=${encodeURIComponent(
                    email,
                  )}`}
                >
                  {t('Forgot password')}
                </a>
              </Text.Body2>
            </div>
          )}
          <Button.ActionButton
            type="submit"
            disabled={!email.length || (showPasswordField && !password.length)}
            fullWidth
            handleClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              handleSubmit(e);
            }}
            status={Button.Status.DEFAULT}
          >
            {buttonText || (showPasswordField ? t('Login') : t('Next'))}
          </Button.ActionButton>
          <HiddenFields fields={fields} />
        </form>
      </div>
      {!hideFooter && (
        <Footer
          fedLoginLink={fedLoginLink || '/'}
          registerLink={registerLink || '/'}
        />
      )}
    </PageWrapper>
  );
};

export default LoginForm;
